<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import ConfigForm from '../components/ConfigForm.vue';
import { createConfigDraft } from '../components/configDraft';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import PaymentAccountsEditor from '../components/PaymentAccountsEditor.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const configState = createConfigDraft(props.bot);
const { draft, setValue, fieldError } = configState;

// Safety-side settings. Field definitions mirror HandoffTab.vue; only
// `section` differs. The customer-facing handoff fields live in
// TransferHubTab.vue.
const fields = [
  {
    path: 'vision.enabled',
    section: 'VISION',
    label: 'AI_AGENT.HANDOFF.FIELDS.VISION_ENABLED',
    type: 'switch',
    // Server default: bots saved before vision existed still read images.
    default: true,
  },
  {
    path: 'vision.payment_proof_message',
    section: 'VISION',
    label: 'AI_AGENT.HANDOFF.FIELDS.VISION_PAYMENT_PROOF_MESSAGE',
    type: 'textarea',
    help: 'AI_AGENT.HANDOFF.FIELDS.VISION_PAYMENT_PROOF_MESSAGE_HELP',
  },
  {
    path: 'guards.repeat',
    section: 'GUARDS',
    label: 'AI_AGENT.HANDOFF.FIELDS.GUARDS_REPEAT',
    type: 'switch',
  },
  {
    path: 'guards.fabricated_numbers',
    section: 'GUARDS',
    label: 'AI_AGENT.HANDOFF.FIELDS.GUARDS_FABRICATED_NUMBERS',
    type: 'switch',
  },
  {
    path: 'guards.blocked_reply_message',
    section: 'GUARDS',
    label: 'AI_AGENT.HANDOFF.FIELDS.GUARDS_BLOCKED_REPLY_MESSAGE',
    type: 'text',
  },
  {
    path: 'handoff.guardrail_enabled',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.GUARDRAIL_ENABLED',
    type: 'switch',
  },
  {
    path: 'handoff.guardrail_message',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.GUARDRAIL_MESSAGE',
    type: 'textarea',
  },
  {
    path: 'confidence.answer_min',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.ANSWER_MIN',
    type: 'number',
  },
  {
    path: 'confidence.log_unanswered_below',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.LOG_UNANSWERED_BELOW',
    type: 'number',
  },
  {
    path: 'guards.min_digits',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.GUARDS_MIN_DIGITS',
    type: 'number',
  },
  {
    path: 'burst.enabled',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.BURST_ENABLED',
    type: 'switch',
  },
  {
    path: 'burst.quiet_seconds',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.BURST_QUIET_SECONDS',
    type: 'number',
  },
  {
    path: 'burst.max_wait_seconds',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.BURST_MAX_WAIT_SECONDS',
    type: 'number',
  },
  {
    path: 'limits.monthly_message_cap',
    section: 'ADVANCED',
    label: 'AI_AGENT.LIMITS.FIELDS.MONTHLY_MESSAGE_CAP',
    type: 'number',
    help: 'AI_AGENT.LIMITS.FIELDS.CAP_HELP',
  },
  {
    path: 'limits.monthly_token_cap',
    section: 'ADVANCED',
    label: 'AI_AGENT.LIMITS.FIELDS.MONTHLY_TOKEN_CAP',
    type: 'number',
    help: 'AI_AGENT.LIMITS.FIELDS.CAP_HELP',
  },
  {
    path: 'limits.max_messages_per_conversation',
    section: 'ADVANCED',
    label: 'AI_AGENT.LIMITS.FIELDS.MAX_MESSAGES_PER_CONVERSATION',
    type: 'number',
  },
  {
    path: 'limits.farewell_message',
    section: 'ADVANCED',
    label: 'AI_AGENT.LIMITS.FIELDS.FAREWELL_MESSAGE',
    type: 'textarea',
  },
];

// Built lazily: the account locale may load after this tab is set up.
const sections = computed(() => [
  {
    key: 'PAYMENTS',
    icon: 'i-lucide-landmark',
    title: t('AI_AGENT.HANDOFF.SECTIONS.PAYMENTS'),
    description: t('AI_AGENT.HANDOFF.SECTIONS.PAYMENTS_HELP'),
  },
  {
    key: 'VISION',
    icon: 'i-lucide-scan-eye',
    title: t('AI_AGENT.HANDOFF.SECTIONS.VISION'),
    description: t('AI_AGENT.HANDOFF.SECTIONS.VISION_HELP'),
  },
  {
    key: 'GUARDS',
    icon: 'i-lucide-shield-alert',
    title: t('AI_AGENT.HANDOFF.SECTIONS.GUARDS'),
    description: t('AI_AGENT.HANDOFF.SECTIONS.GUARDS_HELP'),
  },
  {
    key: 'ADVANCED',
    icon: 'i-lucide-sliders-horizontal',
    title: t('AI_AGENT.STUDIO.ADVANCED'),
    description: t('AI_AGENT.STUDIO.ADVANCED_HELP'),
    collapsible: true,
  },
]);

// Official payment accounts + the outbound payment-account guard (payments.*).
// A config saved before this section existed has no payments key: the server
// default (guard on, empty list) applies until the first save.
const paymentGuardEnabled = computed({
  get: () => draft.payments?.guard_enabled !== false,
  set: value => setValue({ path: 'payments.guard_enabled' }, value),
});

const paymentAccounts = computed({
  get: () =>
    Array.isArray(draft.payments?.accounts) ? draft.payments.accounts : [],
  set: value => setValue({ path: 'payments.accounts' }, value),
});

const paymentBlockedMessage = computed({
  get: () => draft.payments?.blocked_message ?? '',
  set: value => setValue({ path: 'payments.blocked_message' }, value),
});
</script>

<template>
  <ConfigForm
    :bot="bot"
    :state="configState"
    :fields="fields"
    :sections="sections"
  >
    <template #section-PAYMENTS>
      <div
        class="flex items-start justify-between gap-4 px-3 py-2.5 rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <div class="flex flex-col gap-0.5 min-w-0">
          <span class="text-label text-n-slate-12">
            {{ $t('AI_AGENT.HANDOFF.PAYMENTS.GUARD_ENABLED') }}
          </span>
          <span class="text-label-small text-n-slate-10">
            {{ $t('AI_AGENT.HANDOFF.PAYMENTS.GUARD_ENABLED_HELP') }}
          </span>
        </div>
        <Switch
          v-model="paymentGuardEnabled"
          class="mt-1"
          data-testid="field-payments.guard_enabled"
        />
      </div>
      <PaymentAccountsEditor
        v-model="paymentAccounts"
        :error-for="fieldError"
        class="pt-4"
      />
      <TextArea
        id="field-payments.blocked_message"
        v-model="paymentBlockedMessage"
        class="pt-4"
        :label="$t('AI_AGENT.HANDOFF.PAYMENTS.BLOCKED_MESSAGE')"
        :placeholder="
          $t('AI_AGENT.HANDOFF.PAYMENTS.BLOCKED_MESSAGE_PLACEHOLDER')
        "
        :max-length="500"
        :message="
          fieldError('payments.blocked_message') ||
          $t('AI_AGENT.HANDOFF.PAYMENTS.BLOCKED_MESSAGE_HELP')
        "
        :message-type="
          fieldError('payments.blocked_message') ? 'error' : 'info'
        "
        data-testid="field-payments.blocked_message"
        auto-height
      />
    </template>
  </ConfigForm>
</template>
